import { useCallback, useMemo } from "react";

import { Autocomplete } from "../app/Autocomplete";
import { runMatchState, shortVehicleNumber } from "../app/format";
import { useLines } from "../config/LinesProvider";
import type { Journey } from "../contract/types";
import { LineBadge } from "./bits";
import { journeySearchTerms } from "./journeySearch";

/**
 * Sélecteur de course/véhicule des outils — `<Autocomplete>` branché sur les
 * courses en circulation. Recherche multi-critères (n° de véhicule, n° de
 * ligne, n° de sortie / service : voir `journeySearchTerms`).
 *
 * Deux modes, hérités d'`<Autocomplete>` :
 *  - sans `selected` : le choix appelle `onSelect(course)` et le champ se vide
 *    (sélecteur « d'action », ex. ouvrir un véhicule dans le panneau) ;
 *  - avec `selected` : le champ affiche la course choisie (ex. course B du
 *    croisement) ; `onSelect(null)` quand l'utilisateur retape.
 */
export function JourneyPicker({
  label,
  journeys,
  onSelect,
  selected = null,
  placeholder = "N° de véhicule, de ligne, de sortie ou de service…",
}: {
  label: string;
  journeys: readonly Journey[];
  onSelect: (journey: Journey | null) => void;
  selected?: Journey | null;
  placeholder?: string;
}) {
  const lines = useLines();

  // Tri métier (ligne, puis sortie) : à rang de correspondance égal, l'ordre
  // d'origine est conservé par `filterAndRank`.
  const sorted = useMemo(
    () =>
      [...journeys].sort(
        (a, b) =>
          lines.code(a.lineId).localeCompare(lines.code(b.lineId), "fr", { numeric: true }) ||
          // sorties rapprochées d'abord ; « sortie ? » en fin de liste
          Number(a.runNumber === null) - Number(b.runNumber === null) ||
          (a.runNumber ?? "").localeCompare(b.runNumber ?? "", "fr", { numeric: true }),
      ),
    [journeys, lines],
  );

  const getSearchTerms = useCallback(
    (j: Journey) => journeySearchTerms(j, lines.code(j.lineId)),
    [lines],
  );

  return (
    <Autocomplete
      label={label}
      options={sorted}
      getKey={(j) => j.id}
      getSearchTerms={getSearchTerms}
      onSelect={onSelect}
      selected={selected}
      getSelectedText={(j) => journeyLabel(j, lines.code(j.lineId))}
      placeholder={placeholder}
      emptyText="Aucun véhicule ne correspond."
      renderOption={(j) => <JourneyOption journey={j} />}
    />
  );
}

/** « T3 · bus 2317 · sortie 04 » — texte du champ une fois la course choisie. */
export function journeyLabel(j: Journey, lineCode: string): string {
  const parts = [lineCode, `bus ${shortVehicleNumber(j.vehicle?.displayNumber)}`];
  const run = runMatchState(j.runId, j.runConfidence);
  if (run.matched) parts.push(`sortie ${j.runNumber ?? run.text}`);
  return parts.join(" · ");
}

function JourneyOption({ journey: j }: { journey: Journey }) {
  const run = runMatchState(j.runId, j.runConfidence);
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
      <LineBadge lineId={j.lineId} size={22} />
      <span style={{ display: "flex", flexDirection: "column", minWidth: 0, lineHeight: 1.25 }}>
        <span className="mono" style={{ fontWeight: 700, fontSize: "0.78rem" }}>
          {shortVehicleNumber(j.vehicle?.displayNumber)}
          <span className="has-text-grey" style={{ fontWeight: 400, marginLeft: 8 }}>
            {run.matched ? `sortie ${j.runNumber ?? run.text}` : "sortie ?"}
            {j.serviceId ? ` · service ${j.serviceId}` : ""}
          </span>
        </span>
        <span
          className="has-text-grey"
          style={{ fontSize: "0.7rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {j.headsign || "—"}
        </span>
      </span>
    </span>
  );
}
